import { Link, useParams } from "react-router";
import { Alerta, Adjuntos, Badge, Button, Card, DataList, Desplegable, Dialog, MetricCard, Row, Spinner, Stack, TextField } from "@/components";
import type { Cobro, Cuota, Devolucion } from "@/lib/api";
import { etiquetaPeriodo, formatIndice } from "@/lib/cac";
import { formatFecha, formatMoneda } from "@/lib/format";
import { formatTelefono } from "@/lib/telefonos";
import {
  ETIQUETA_ESTADO_CUOTA, ETIQUETA_ESTADO_VENTA, ETIQUETA_MEDIO, ETIQUETA_MODALIDAD, montoPositivo, nombreCuota, nombreUnidades, TONO_ESTADO_CUOTA, TONO_ESTADO_VENTA,
} from "@/lib/ventas";
import { FormularioCobro } from "./FormularioCobro";
import { FormularioPlan } from "./FormularioPlan";
import { FormularioVenta } from "./FormularioVenta";
import { useVentaTab } from "./useVentaTab";

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="contents">
      <dt className="text-texto-suave">{etiqueta}</dt>
      <dd className="break-words">{valor}</dd>
    </div>
  );
}

function Confirmar({ texto, error, cargando, onConfirmar, onCancelar }: { texto: string; error: string | null; cargando: boolean; onConfirmar: () => void; onCancelar: () => void }) {
  return (
    <Stack espacio={4}>
      <p>{texto}</p>
      <Alerta texto={error} />
      <Row justificar="fin" espacio={2}>
        <Button variante="secundario" onClick={onCancelar}>
          Cancelar
        </Button>
        <Button variante="peligro" cargando={cargando} onClick={onConfirmar}>
          Borrar
        </Button>
      </Row>
    </Stack>
  );
}

const TITULO_TRANSICION = { boleto: "Firmar boleto", escritura: "Escriturar", desistir: "Desistir la venta" } as const;

/** `key={id}` fuerza el remount al cambiar de venta (mismo motivo que GastoTab). */
export function VentaTab() {
  const { id } = useParams();
  return <Ficha key={id} />;
}

function Ficha() {
  const t = useVentaTab();
  const v = t.venta;
  const d = t.dialogo;

  if (t.cargando) {
    return (
      <div className="grid place-items-center py-10 text-primario">
        <Spinner />
      </div>
    );
  }
  if (t.errorCarga || !v) {
    return (
      <Stack espacio={3}>
        <Alerta texto={t.errorCarga ?? "No existe esa venta"} />
        <Row espacio={3}>
          <Button variante="secundario" onClick={t.reintentar}>
            Reintentar
          </Button>
          <Link to="/ventas" className="text-primario underline-offset-2 hover:underline">
            Volver a ventas
          </Link>
        </Row>
      </Stack>
    );
  }

  const m = v.moneda_cuenta;
  const plata = (monto: string) => formatMoneda(monto, m);
  const conCac = v.modalidad === "ARS_CAC";
  // En una venta en USD lo "en cuenta" coincide con lo "en USD": la columna sólo aporta en las ventas en pesos.
  const enCuenta = m === "ARS";
  const c = v.comprador;
  const transicion = d?.tipo === "boleto" || d?.tipo === "escritura" || d?.tipo === "desistir" ? d.tipo : null;

  const columnasPlan = [
    { clave: "cuota", titulo: "Cuota", render: (q: Cuota) => nombreCuota(q) },
    { clave: "vencimiento", titulo: "Vencimiento", render: (q: Cuota) => formatFecha(q.vencimiento) },
    ...(conCac
      ? [
          { clave: "base", titulo: "Base", render: (q: Cuota) => plata(q.monto_base) },
          {
            clave: "actualizado",
            titulo: "Actualizado",
            render: (q: Cuota) => (
              <Stack espacio={1}>
                <Row espacio={2} envolver>
                  <span>{q.monto_actualizado === null ? "Sin índice" : plata(q.monto_actualizado)}</span>
                  {q.provisorio && <Badge tono="alerta">Provisorio</Badge>}
                </Row>
                {q.cac_aplicado && <span className="text-chico text-texto-suave">{`CAC ${etiquetaPeriodo(q.cac_aplicado.periodo)}`}</span>}
              </Stack>
            ),
          },
        ]
      : [{ clave: "monto", titulo: "Monto", render: (q: Cuota) => plata(q.monto_base) }]),
    { clave: "cobrado", titulo: "Cobrado", render: (q: Cuota) => plata(q.cobrado) },
    { clave: "saldo", titulo: "Saldo", render: (q: Cuota) => (q.saldo === null ? "—" : plata(q.saldo)) },
    { clave: "estado", titulo: "Estado", render: (q: Cuota) => <Badge tono={TONO_ESTADO_CUOTA[q.estado]}>{ETIQUETA_ESTADO_CUOTA[q.estado]}</Badge> },
  ];

  const accionesCuota = (q: Cuota) => (
    <>
      {q.cobros.length > 0 && (
        <Button variante="fantasma" tamanio="sm" aria-label={`Ver cobros de ${nombreCuota(q)}`} aria-expanded={t.cuotasAbiertas.has(q.id)} onClick={() => t.alternarCuota(q.id)}>
          {`Cobros (${q.cobros.length})`}
        </Button>
      )}
      {t.puede.cobrar(q) && (
        <Button tamanio="sm" aria-label={`Cobrar ${nombreCuota(q)}`} onClick={() => t.abrirCobro(q)}>
          Cobrar
        </Button>
      )}
      {t.puede.editarCuota(q) && (
        <Button variante="secundario" tamanio="sm" aria-label={`Editar ${nombreCuota(q)}`} onClick={() => t.abrirEditarCuota(q)}>
          Editar
        </Button>
      )}
    </>
  );

  const columnasMovimiento = <T extends Cobro | Devolucion>() => [
    { clave: "fecha", titulo: "Fecha", render: (x: T) => formatFecha(x.fecha) },
    { clave: "monto", titulo: "Monto", render: (x: T) => formatMoneda(x.monto, x.moneda) },
    ...(enCuenta ? [{ clave: "cuenta", titulo: "En cuenta", render: (x: T) => plata(x.monto_en_cuenta) }] : []),
    { clave: "usd", titulo: "En USD", render: (x: T) => formatMoneda(x.monto_usd, "USD") },
    { clave: "medio", titulo: "Medio", render: (x: T) => ETIQUETA_MEDIO[x.medio] },
    { clave: "nota", titulo: "Nota", render: (x: T) => x.nota ?? "—" },
  ];

  const accionesCobro = (q: Cuota) => (cb: Cobro) => (
    <>
      <Button variante="secundario" tamanio="sm" aria-label={`Editar cobro del ${formatFecha(cb.fecha)}`} onClick={() => t.abrirCobro(q, cb)}>
        Editar
      </Button>
      <Button variante="fantasma" tamanio="sm" aria-label={`Borrar cobro del ${formatFecha(cb.fecha)}`} onClick={() => t.abrirBorrarCobro(q, cb)}>
        Borrar
      </Button>
    </>
  );

  const accionesDevolucion = (x: Devolucion) => (
    <>
      <Button variante="secundario" tamanio="sm" aria-label={`Editar devolución del ${formatFecha(x.fecha)}`} onClick={() => t.abrirDevolucion(x)}>
        Editar
      </Button>
      <Button variante="fantasma" tamanio="sm" aria-label={`Borrar devolución del ${formatFecha(x.fecha)}`} onClick={() => t.abrirBorrarDevolucion(x)}>
        Borrar
      </Button>
    </>
  );

  const tituloMovimiento =
    d?.tipo === "cobro" ? (d.cobro ? "Editar cobro" : "Registrar cobro") : d?.tipo === "devolucion" ? (d.devolucion ? "Editar devolución" : "Registrar devolución") : "";

  return (
    <Stack espacio={6}>
      <Stack espacio={2}>
        <Link to="/ventas" className="text-chico text-primario underline-offset-2 hover:underline">
          ← Ventas
        </Link>
        <Row justificar="entre" envolver espacio={3}>
          <Stack espacio={1}>
            <Row espacio={2} envolver>
              <h1 className="text-pantalla font-light tracking-[-0.03em]">{`Venta ${v.numero}`}</h1>
              <Badge tono={TONO_ESTADO_VENTA[v.estado]}>{ETIQUETA_ESTADO_VENTA[v.estado]}</Badge>
            </Row>
            <p className="text-texto-suave">{nombreUnidades(v.unidades)}</p>
          </Stack>
          {t.esAdmin && (
            <Row espacio={2} envolver>
              {t.puede.firmarBoleto && <Button onClick={() => t.abrirTransicion("boleto")}>Firmar boleto</Button>}
              {t.puede.escriturar && <Button onClick={() => t.abrirTransicion("escritura")}>Escriturar</Button>}
              {t.puede.desistir && (
                <Button variante="secundario" onClick={() => t.abrirTransicion("desistir")}>
                  Desistir
                </Button>
              )}
              <Button variante="secundario" onClick={t.abrirEditar}>
                Editar
              </Button>
              {t.puede.borrar && (
                <Button variante="fantasma" onClick={t.abrirBorrar}>
                  Borrar
                </Button>
              )}
            </Row>
          )}
        </Row>
      </Stack>

      <Card>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-chico">
          <dt className="text-texto-suave">Comprador</dt>
          <dd className="break-words">
            <Stack espacio={1}>
              <Link to="/compradores" className="text-primario underline-offset-2 hover:underline">
                {c.nombre}
              </Link>
              {(c.documento || c.telefono) && <span className="text-texto-suave">{[c.documento, c.telefono && formatTelefono(c.telefono)].filter(Boolean).join(" · ")}</span>}
            </Stack>
          </dd>
          <Dato etiqueta="Fecha" valor={formatFecha(v.fecha)} />
          <Dato etiqueta="Modalidad" valor={ETIQUETA_MODALIDAD[v.modalidad]} />
          {v.cac_origen && <Dato etiqueta="Índice" valor={`Origen CAC ${etiquetaPeriodo(v.cac_origen.periodo)} · ${formatIndice(v.cac_origen.valor)}`} />}
          <Dato etiqueta="Precio" valor={plata(v.precio)} />
          {v.boleto_fecha && <Dato etiqueta="Firma del boleto" valor={formatFecha(v.boleto_fecha)} />}
          {v.escritura_fecha && <Dato etiqueta="Escritura" valor={formatFecha(v.escritura_fecha)} />}
          {v.desistida_fecha && <Dato etiqueta="Desistimiento" valor={formatFecha(v.desistida_fecha)} />}
          {v.desistida_motivo && <Dato etiqueta="Motivo" valor={v.desistida_motivo} />}
          <Dato etiqueta="Notas" valor={v.notas ?? "—"} />
        </dl>
      </Card>

      <div className="grid gap-3 sm:grid-cols-3">
        {v.estado === "desistida" ? (
          <>
            <MetricCard titulo="Cobrado" valor={plata(v.cobrado)} />
            <MetricCard titulo="Devuelto" valor={plata(v.devuelto)} />
            <MetricCard titulo="Retenido" valor={plata(v.retenido)} detalle="Cobrado menos devuelto" />
          </>
        ) : (
          <>
            <MetricCard titulo="Cobrado" valor={plata(v.cobrado)} />
            <MetricCard titulo="Saldo" valor={plata(v.saldo)} detalle={v.saldo_incompleto ? "incompleto: falta un índice CAC" : undefined} />
            <MetricCard titulo="Vencido" valor={plata(v.vencido)} className={montoPositivo(v.vencido) ? "text-peligro" : undefined} />
          </>
        )}
      </div>

      <Stack espacio={3}>
        <Row justificar="entre" envolver espacio={3}>
          <h2 className="text-titulo font-light tracking-tight">Plan de cuotas</h2>
          {t.puede.armarPlan && <Button onClick={t.abrirPlan}>Armar plan</Button>}
        </Row>
        {t.esAdmin && t.activa && t.conCobros && <p className="text-chico text-texto-suave">Con cobros cargados el plan no se rearma.</p>}
        <DataList etiqueta="Plan de cuotas" columnas={columnasPlan} filas={v.cuotas} claveFila={(q) => q.id} acciones={accionesCuota} vacio="Todavía no hay plan de cuotas." />
        {v.cuotas
          .filter((q) => t.cuotasAbiertas.has(q.id))
          .map((q) => (
            <Stack key={q.id} espacio={2}>
              <h3 className="font-medium">{`Cobros de ${nombreCuota(q)}`}</h3>
              <DataList
                etiqueta={`Cobros de ${nombreCuota(q)}`}
                columnas={columnasMovimiento<Cobro>()}
                filas={q.cobros}
                claveFila={(cb) => cb.id}
                acciones={t.puede.editarCobros ? accionesCobro(q) : undefined}
                vacio="Sin cobros."
              />
              {q.cobros.map((cb) => (
                <Desplegable key={cb.id} titulo={`Recibos del cobro del ${formatFecha(cb.fecha)} (${cb.adjuntos.length})`}>
                  <Adjuntos
                    etiqueta={`Recibos del cobro del ${formatFecha(cb.fecha)}`}
                    adjuntos={cb.adjuntos}
                    rutaDescarga={t.rutaDescarga}
                    puedeEditar={t.esAdmin}
                    onSubir={t.subirAlCobro(q, cb)}
                    onBorrar={t.borrarAdjunto}
                    subiendo={t.subiendo}
                    error={null}
                  />
                </Desplegable>
              ))}
            </Stack>
          ))}
      </Stack>

      {v.estado === "desistida" && (
        <Stack espacio={3}>
          <Row justificar="entre" envolver espacio={3}>
            <h2 className="text-titulo font-light tracking-tight">Devoluciones</h2>
            {t.puede.devolver && <Button onClick={() => t.abrirDevolucion()}>Registrar devolución</Button>}
          </Row>
          <DataList
            etiqueta="Devoluciones"
            columnas={columnasMovimiento<Devolucion>()}
            filas={v.devoluciones}
            claveFila={(x) => x.id}
            acciones={t.puede.devolver ? accionesDevolucion : undefined}
            vacio="Todavía no hay devoluciones."
          />
        </Stack>
      )}

      <Adjuntos etiqueta="Boleto y documentos" adjuntos={v.adjuntos} rutaDescarga={t.rutaDescarga} puedeEditar={t.esAdmin} onSubir={t.subirAVenta} onBorrar={t.borrarAdjunto} subiendo={t.subiendo} error={t.errorAdjuntos} />

      <Dialog abierto={d?.tipo === "editar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={t.activa ? "Editar venta" : "Editar notas"}>
        {t.activa ? (
          <FormularioVenta
            form={t.formV}
            cambiar={t.cambiarV}
            errores={t.erroresV}
            compradores={t.compradores}
            unidades={t.unidadesElegibles}
            edicion
            onSubmit={(e) => void t.guardarVenta(e)}
            onCancelar={t.cerrar}
            guardando={t.guardandoVenta}
            error={t.errorVenta}
          />
        ) : (
          <form onSubmit={(e) => void t.guardarVenta(e)} noValidate>
            <Stack espacio={4}>
              <p className="text-chico text-texto-suave">{`La venta está ${ETIQUETA_ESTADO_VENTA[v.estado].toLowerCase()}: sólo se editan las notas.`}</p>
              <TextField etiqueta="Notas" value={t.formV.notas} onChange={(e) => t.cambiarV("notas", e.target.value)} />
              <Alerta texto={t.errorVenta} />
              <Row justificar="fin" espacio={2}>
                <Button variante="secundario" onClick={t.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.guardandoVenta}>
                  Guardar
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar venta">
        <Confirmar
          texto={`¿Borrar la venta ${v.numero}? Las unidades vuelven a estar disponibles y sus archivos se borran.`}
          error={t.errorBorrar}
          cargando={t.borrando}
          onConfirmar={() => void t.confirmarBorrar()}
          onCancelar={t.cerrar}
        />
      </Dialog>

      <Dialog abierto={d?.tipo === "plan"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Plan de cuotas">
        <FormularioPlan
          precio={v.precio}
          monedaCuenta={m}
          form={t.formP}
          cambiar={t.cambiarP}
          erroresPlan={t.erroresP}
          filas={t.filas}
          setFilas={t.setFilas}
          erroresFilas={t.erroresF}
          generando={t.generando}
          errorVistaPrevia={t.errorVistaPrevia}
          generar={() => void t.generar()}
          onSubmit={(e) => void t.guardarPlan(e)}
          onCancelar={t.cerrar}
          guardando={t.guardandoPlan}
          error={t.errorPlan}
        />
      </Dialog>

      <Dialog abierto={d?.tipo === "editar-cuota"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Editar cuota">
        <form onSubmit={(e) => void t.guardarCuota(e)} noValidate>
          <Stack espacio={4}>
            <TextField
              etiqueta="Vencimiento"
              type="date"
              value={t.formQ.vencimiento}
              onChange={(e) => t.cambiarQ("vencimiento", e.target.value)}
              error={t.erroresQ.vencimiento}
              ayuda="Para cambiar montos, rearmá el plan."
            />
            <Alerta texto={t.errorCuota} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardandoCuota}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "cobro" || d?.tipo === "devolucion"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={tituloMovimiento}>
        <FormularioCobro
          titulo={tituloMovimiento}
          form={t.formM}
          cambiar={t.cambiarM}
          errores={t.erroresM}
          monedaCuenta={m}
          saldo={d?.tipo === "cobro" && d.cobro === null ? d.cuota.saldo : null}
          equivalente={t.equivalente}
          onSubmit={(e) => void t.guardarMovimiento(e)}
          onCancelar={t.cerrar}
          guardando={t.guardandoMovimiento}
          error={t.errorMovimiento}
          opcionesCaja={t.opcionesCajaMovimiento}
        />
      </Dialog>

      <Dialog abierto={transicion !== null} onCambio={(abierto) => !abierto && t.cerrar()} titulo={transicion ? TITULO_TRANSICION[transicion] : ""}>
        <form onSubmit={(e) => void t.confirmarTransicion(e)} noValidate>
          <Stack espacio={4}>
            {transicion === "desistir" && (
              <p className="text-chico text-texto-suave">Las unidades vuelven a estar disponibles. Los cobros quedan en la venta; las devoluciones se cargan después.</p>
            )}
            <TextField etiqueta="Fecha" type="date" value={t.formT.fecha} onChange={(e) => t.cambiarT("fecha", e.target.value)} error={t.erroresT.fecha} />
            {transicion === "desistir" && <TextField etiqueta="Motivo" value={t.formT.motivo} onChange={(e) => t.cambiarT("motivo", e.target.value)} error={t.erroresT.motivo} />}
            <Alerta texto={t.errorTransicion} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" variante={transicion === "desistir" ? "peligro" : "primario"} cargando={t.transicionando}>
                Confirmar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar-cobro"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar cobro">
        {d?.tipo === "borrar-cobro" && (
          <Confirmar
            texto={`¿Borrar el cobro del ${formatFecha(d.cobro.fecha)} por ${formatMoneda(d.cobro.monto, d.cobro.moneda)}? Sus recibos también se borran.`}
            error={t.errorBorrarMovimiento}
            cargando={t.borrandoMovimiento}
            onConfirmar={() => void t.confirmarBorrarMovimiento()}
            onCancelar={t.cerrar}
          />
        )}
      </Dialog>

      <Dialog abierto={d?.tipo === "borrar-devolucion"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar devolución">
        {d?.tipo === "borrar-devolucion" && (
          <Confirmar
            texto={`¿Borrar la devolución del ${formatFecha(d.devolucion.fecha)} por ${formatMoneda(d.devolucion.monto, d.devolucion.moneda)}?`}
            error={t.errorBorrarMovimiento}
            cargando={t.borrandoMovimiento}
            onConfirmar={() => void t.confirmarBorrarMovimiento()}
            onCancelar={t.cerrar}
          />
        )}
      </Dialog>
    </Stack>
  );
}
